import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAPI } from '../../services/api.service';
import { toast } from 'react-toastify';

export type Phase = 'all' | 'pre' | 'during' | 'post';

export type ParticipantPreview = {
    _id: string;
    firstname?: string;
    lastname?: string;
    email?: string;
};

export type AssignmentPreview = {
    _id?: string;
    title?: string;
    groupId?: string;
    visibleFrom?: string;
    visibleTo?: string;
};

export type ReviewRow = {
    _id: string;
    templateId?: string;
    assignmentId?: String,
    participant?: ParticipantPreview;
    assignment?: AssignmentPreview;
    group?: { _id?: string; title?: string } | null;
    responses?: any;
    duringNotes?: any[];
    attachments?: any[];
    status?: string;
    submittedAt?: string | Date | null;
    lastSavedAt?: string | Date | null;
    version?: number;
    createdAt?: string;
};

type FetchOpts = {
    groupId?: string | null;
    page?: number;
    limit?: number;
    search?: string;
    phase?: Phase;
};

export const useFetchResponsesForAdmin = (initial?: FetchOpts) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}') as { id?: string; role?: string };
    const userId = user?.id;
    const [page, setPage] = useState<number>(initial?.page ?? 1);
    const [limit, setLimit] = useState<number>(initial?.limit ?? 10);
    const [groupId, setGroupId] = useState<string | null>(initial?.groupId ?? null);
    const [phase, setPhase] = useState<Phase>(initial?.phase ?? 'all');
    const [search, setSearch] = useState<string>(initial?.search ?? '');
    const [debouncedSearch, setDebouncedSearch] = useState<string>(initial?.search ?? '');
    const [data, setData] = useState<ReviewRow[]>([]);
    const [groupMeta, setGroupMeta] = useState<any | null>(null);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // debounce search (300ms)
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const fetchPage = useCallback(async (p = page, l = limit, gid = groupId, ph = phase, s = debouncedSearch) => {
        if (!userId) {
            setError('User not logged in');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params: any = {
                userId,
                page: p,
                limit: l,
                phase: ph
            };
            if (gid) params.groupId = gid;
            if (s) params.search = s;

            const res = await getAPI<{ data: any, message: string }>('book-review/review-response/group/phase-wise-responses', params);
            // expected shape: { status:200, data: { group, reviews }, total }
            const payload = res?.data ?? res;
            const group = payload?.data?.group ?? null;
            const reviews = payload?.data?.reviews ?? [];
            const tot = payload?.data?.total ?? (Array.isArray(reviews) ? reviews.length : 0);

            setData(Array.isArray(reviews) ? reviews : []);
            setGroupMeta(group ?? null);
            setTotal(Number(tot || 0));
        } catch (err: any) {
            console.error('fetch submitted responses error', err);
            setError(err?.response?.data?.message || err?.message || 'Failed to fetch responses');
            toast.error(err?.response?.data?.message || 'Failed to fetch responses');
        } finally {
            setLoading(false);
        }
    }, [userId, page, limit, groupId, phase, debouncedSearch]);

    // auto fetch when key controls change
    useEffect(() => {
        // when filters change reset to page 1
        setPage(1);
        (async () => {
            await fetchPage(1, limit, groupId, phase, debouncedSearch);
        })();
    }, [groupId, phase, debouncedSearch, limit, fetchPage]);

    // explicit page change effect
    useEffect(() => {
        (async () => {
            await fetchPage(page, limit, groupId, phase, debouncedSearch);
        })();
    }, [page]); // eslint-disable-line

    const reload = useCallback(() => fetchPage(page, limit, groupId, phase, debouncedSearch), [fetchPage, page, limit, groupId, phase, debouncedSearch]);

    const setFilters = useCallback((opts: { groupId?: string | null; phase?: Phase; search?: string }) => {
        if (opts.groupId !== undefined) setGroupId(opts.groupId ?? null);
        if (opts.phase) setPhase(opts.phase);
        if (opts.search !== undefined) setSearch(opts.search);
    }, []);

    return {
        data,
        group: groupMeta,
        total,
        loading,
        error,
        page,
        limit,
        setPage,
        setLimit,
        search,
        setSearch,
        phase,
        setPhase,
        groupId,
        setGroupId,
        reload,
        setFilters
    };
};
